import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { addProject, updateProject, deleteProject } from '../../../store/slices/projectsSlice';
import Icon from '../../../components/AppIcon';
import ProjectForm from './ProjectForm';

const ProjectsAdmin = () => {
  const dispatch = useDispatch();
  const { projects } = useSelector(state => state.projects);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);

  const handleAddProject = (projectData) => {
    dispatch(addProject(projectData));
    setIsFormOpen(false);
  };

  const handleUpdateProject = (projectData) => {
    dispatch(updateProject(projectData));
    setEditingProject(null);
    setIsFormOpen(false);
  };

  const handleDeleteProject = (projectId) => {
    if (window.confirm('Are you sure you want to delete this project?')) {
      dispatch(deleteProject(projectId));
    }
  };

  const handleEditProject = (project) => {
    setEditingProject(project);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditingProject(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-foreground">Projects Management</h2>
        <button
          onClick={() => setIsFormOpen(true)}
          className="bg-primary text-primary-foreground px-4 py-2 rounded-md hover:bg-primary/90 transition-colors font-medium flex items-center"
        >
          <Icon name="Plus" size={16} className="mr-2" />
          Add Project
        </button>
      </div>

      {/* Projects Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {projects.map((project) => (
          <div key={project.id} className="bg-surface rounded-lg p-6 security-shadow">
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name={project.icon} size={24} className="text-primary" />
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleEditProject(project)}
                  className="p-2 text-text-secondary hover:text-primary transition-colors"
                  title="Edit"
                >
                  <Icon name="Edit" size={16} />
                </button>
                <button
                  onClick={() => handleDeleteProject(project.id)}
                  className="p-2 text-text-secondary hover:text-destructive transition-colors"
                  title="Delete"
                >
                  <Icon name="Trash" size={16} />
                </button>
              </div>
            </div>
            
            <h3 className="text-lg font-semibold text-foreground mb-2 line-clamp-2">
              {project.title}
            </h3>
            
            <p className="text-text-secondary mb-3 line-clamp-3 text-sm">
              {project.description}
            </p>
            
            <div className="space-y-2">
              <div className="flex flex-wrap gap-1">
                {project.tools.slice(0, 3).map((tool, index) => (
                  <span 
                    key={index}
                    className="inline-flex items-center px-2 py-1 rounded text-xs bg-muted text-muted-foreground"
                  >
                    {tool}
                  </span>
                ))}
                {project.tools.length > 3 && (
                  <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-muted text-muted-foreground">
                    +{project.tools.length - 3} more
                  </span>
                )}
              </div>
            </div>
            
            {project.github && (
              <div className="mt-3 pt-3 border-t border-border">
                <a
                  href={project.github}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:text-primary/80 text-sm flex items-center"
                >
                  <Icon name="Github" size={14} className="mr-1" />
                  GitHub
                </a>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Add/Edit Form Modal */}
      {isFormOpen && (
        <ProjectForm
          project={editingProject}
          onSave={editingProject ? handleUpdateProject : handleAddProject}
          onClose={handleCloseForm}
        />
      )}
    </div>
  );
};

export default ProjectsAdmin; 